import { useEffect, useMemo, useRef, useState } from "react";
import { useInteractionReady } from "../ui/InteractionGate";
import { partsImageSource } from "../../lib/partsImageSource";

const PROXY_BASE = `${import.meta.env['VITE_SUPABASE_URL']}/functions/v1/cdn-img-proxy`;

interface ProtectedImageProps {
  src: string;
  alt: string;
  className?: string;
  objectFit?: "contain" | "cover";
  loading?: "eager" | "lazy";
}

/**
 * Native image decoding with explicit offscreen deferral. The overlay
 * deters dragging/right-click downloads, not inspection through DevTools.
 */
export default function ProtectedImage({
  src,
  alt,
  className = "",
  objectFit = "contain",
  loading = "lazy",
}: ProtectedImageProps) {
  const imageSrc = useMemo(() => partsImageSource(src, PROXY_BASE), [src]);
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const engaged = useInteractionReady();
  const ready = loading === "eager" || visible;

  useEffect(() => {
    if (ready) return;
    const el = ref.current;
    if (!el) return;
    // Initial visible photos never wait for a click; offscreen photos do.
    const bounds = el.getBoundingClientRect();
    if (bounds.bottom > 0 && bounds.top < window.innerHeight &&
        bounds.right > 0 && bounds.left < window.innerWidth) {
      setVisible(true);
      return;
    }
    if (typeof IntersectionObserver === "undefined") {
      if (engaged) setVisible(true);
      return;
    }
    if (!engaged) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: "200px 0px" });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ready, engaged]);

  return (
    <div
      ref={ref}
      role="img"
      aria-label={alt}
      className={`relative select-none ${className}`}
      style={{
        WebkitUserSelect: "none",
        userSelect: "none",
        WebkitTouchCallout: "none",
      }}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >
      {ready && <img
        src={imageSrc}
        alt=""
        aria-hidden="true"
        draggable={false}
        loading={loading}
        decoding="async"
        className={`absolute inset-0 h-full w-full ${objectFit === "cover" ? "object-cover" : "object-contain"}`}
      />}
      {/* Preserve the existing download deterrent without eager CSS requests. */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ background: "transparent" }}
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
      />
      <noscript>
        <img src={imageSrc} alt={alt} loading={loading} decoding="async" className={`w-full h-full ${objectFit === "cover" ? "object-cover" : "object-contain"}`} />
      </noscript>
    </div>
  );
}